import React, { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import Index from "./pages/Index";
import ScrollToTop from "./components/ScrollToTop";
import ListingLeadGate from "./components/ListingLeadGate";

const About = lazy(() => import("./pages/About"));
const Buy = lazy(() => import("./pages/Buy"));
const Sell = lazy(() => import("./pages/Sell"));
const Contact = lazy(() => import("./pages/Contact"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const BuyerGuide = lazy(() => import("./pages/BuyerGuide"));
const SellerGuide = lazy(() => import("./pages/SellerGuide"));
const Blog = lazy(() => import("./pages/Blog"));
const Financial = lazy(() => import("./pages/Financial"));
const BlogArticle = lazy(() => import("./pages/BlogArticle"));
const ExploreNJ = lazy(() => import("./pages/ExploreNJ"));
const ExploreArticle = lazy(() => import("./pages/ExploreArticle"));
const ExploreFL = lazy(() => import("./pages/ExploreFL"));
const WMarriott = lazy(() => import("./pages/WMarriott"));
const WMarriottGallery = lazy(() => import("./pages/WMarriottGallery"));
const WMarriottInquire = lazy(() => import("./pages/WMarriottInquire"));
const WMarriottThankYou = lazy(() => import("./pages/WMarriottThankYou"));
const WMarriottFloorPlans = lazy(() => import("./pages/WMarriottFloorPlans"));
const Gallery = lazy(() => import("./pages/Gallery"));
const Community = lazy(() => import("./pages/Community"));
const Search = lazy(() => import("./pages/Search"));
const Listings = lazy(() => import("./pages/Listings"));
const NotFound = lazy(() => import("./pages/NotFound"));

const LegacyInsightRedirect = () => {
  const { slug } = useParams();
  return <Navigate to={`/blog/${slug ?? ""}`} replace />;
};

// Legacy URLs from the previous site (still indexed / visited) -> current pages
const COMMUNITY_SLUGS = [
  "princeton", "west-windsor", "hamilton", "robbinsville", "lawrenceville",
  "trenton", "monmouth-county", "ocean-county", "middlesex-county",
];
const LegacyAreaRedirect = () => {
  const { slug = "" } = useParams();
  const clean = slug.replace(/-nj$/, "");
  return <Navigate to={COMMUNITY_SLUGS.includes(clean) ? `/communities/${clean}` : "/"} replace />;
};

const queryClient = new QueryClient();

const PageLoader = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <BrowserRouter>
      <ScrollToTop />
      <ListingLeadGate />
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/about" element={<About />} />
            <Route path="/buy" element={<Buy />} />
            <Route path="/sell" element={<Sell />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/buyer-guide" element={<BuyerGuide />} />
            <Route path="/seller-guide" element={<SellerGuide />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogArticle />} />
            <Route path="/market-insights" element={<Navigate to="/blog" replace />} />
            <Route path="/financial" element={<Financial />} />
            <Route path="/market-insights/:slug" element={<LegacyInsightRedirect />} />
            <Route path="/explore-nj" element={<ExploreNJ />} />
            <Route path="/explore-nj/:slug" element={<ExploreArticle />} />
            <Route path="/explore-fl" element={<ExploreFL />} />
            <Route path="/about-florida" element={<ExploreFL />} />
            <Route path="/wmarriot" element={<WMarriott />} />
            <Route path="/wmarriot/gallery" element={<WMarriottGallery />} />
            <Route path="/wmarriot/inquire" element={<WMarriottInquire />} />
            <Route path="/wmarriot/thank-you" element={<WMarriottThankYou />} />
            <Route path="/wmarriot/floor-plans" element={<WMarriottFloorPlans />} />
            <Route path="/gallery" element={<Gallery />} />
            <Route path="/w-pompano-beach" element={<WMarriott />} />
            <Route path="/w-pompano-beach/gallery" element={<WMarriottGallery />} />
            <Route path="/w-pompano-beach/inquire" element={<WMarriottInquire />} />
            <Route path="/w-pompano-beach/thank-you" element={<WMarriottThankYou />} />
            <Route path="/w-pompano-beach/floor-plans" element={<WMarriottFloorPlans />} />
            <Route path="/communities/:slug" element={<Community />} />
            <Route path="/search" element={<Search />} />
            <Route path="/listings" element={<Listings />} />
            <Route path="/articles" element={<Navigate to="/blog" replace />} />
            <Route path="/articles/:slug" element={<Navigate to="/blog" replace />} />
            <Route path="/areas/:slug" element={<LegacyAreaRedirect />} />
            <Route path="/privacy-policy" element={<Privacy />} />
            <Route path="/terms-and-conditions" element={<Terms />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </TooltipProvider>
    </BrowserRouter>
  </QueryClientProvider>
);

export default App;
